import React, { useState, useEffect, useCallback } from "react";
import {
  CircularProgress,
  Paper,
  Box,
  Typography,
  IconButton,
  Grid,
  Card,
  CardContent,
  Divider,
  Tooltip,
  Button,
  ButtonGroup,
  Chip,
  Switch,
  withStyles,
} from "@material-ui/core";
import { Refresh, ViewModule, ViewList } from "@material-ui/icons";
import { useStyles } from "../../css/muiStyle";
import {
  AlertCompo,
  DateTimePickerUpd,
  NoDataComponent,
  NotAllowedComponent,
} from "../../js/components";
import ContentEditable from "react-contenteditable";
import DynamicTable from "../../compo/DynamicTable";
import Icon from "@mdi/react";
import {
  mdiCheckCircleOutline,
  mdiAlertCircleOutline,
  mdiClockAlertOutline,
  mdiPencilOutline,
  mdiAccount,
  mdiCalendarClock,
} from "@mdi/js";

// Helper function to safely parse dates
const parseDate = (dateStr) => {
  if (!dateStr || dateStr === "—" || dateStr === "") return null;
  try {
    const dateObj = new Date(dateStr.replace(" ", "T"));
    return isNaN(dateObj.getTime()) ? null : dateObj;
  } catch (e) {
    return null;
  }
};

const isExpired = (dateStr) => {
  if (!dateStr || dateStr === "—" || dateStr === "") return false;
  try {
    const dateObj = parseDate(dateStr);
    if (!dateObj) return false;
    return dateObj < new Date();
  } catch (e) {
    return false;
  }
};

// Check if date is in the future (greater than today)
const isFutureDate = (dateStr) => {
  if (!dateStr || dateStr === "—" || dateStr === "") return false;
  try {
    const dateObj = parseDate(dateStr);
    if (!dateObj) return false;
    return dateObj > new Date();
  } catch (e) {
    return false;
  }
};

// Service is stopped only if:
// 1. Status is not 0 (inactive/disabled) AND
// 2. Expiration date is in the future (greater than today)
const isServiceStopped = (status, expireDate) => {
  if (!expireDate || expireDate === "—" || expireDate === "") {
    return false;
  }
  return status !== 1 && isFutureDate(expireDate);
};
const RedSwitch = withStyles({
  switchBase: {
    color: "#bdbdbd",
    "&$checked": {
      color: "#f44336",
    },
    "&$checked + $track": {
      backgroundColor: "#f44336",
    },
  },
  checked: {},
  track: {
    backgroundColor: "#eae7e7",
    opacity: 1,
  },
})(Switch);
export default function ServiceStatus(props) {
  const param = props.param;
  const Gfunc = require("../../Gfunc");
  const classes = useStyles();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [noData, setNoData] = useState(false);
  const [notAllowed, setNotAllowed] = useState(false);
  const [errMessage, setErrMessage] = useState("");
  const [severity, setSeverity] = useState("info");
  const [open, setOpen] = useState(false);
  const [messageA, setMessageA] = useState("");
  const [viewMode, setViewMode] = useState("cards");
  const [selecto, setSelecto] = useState("");
  const [Item, setItem] = useState("");
  const [ItemN, setItemN] = useState("");
  const service = "_srv.php";

  const [focused, setFocused] = useState(false);

  const handleAlert = (open, severity, message) => {
    setOpen(open);
    setSeverity(severity);
    setMessageA(message);
    setTimeout(() => setOpen(false), 5000);
  };

  const fetch = useCallback(async () => {
    setLoading(true);
    const response = await Gfunc.axiosGetAction(service, "get");
    if (response.data.err && response.data.err !== "-1") {
      setErrMessage(response.data.err);
      setNotAllowed(true);
      handleAlert(true, "error", response.data.err);
    } else {
      Gfunc.axiosResponse(response, () => {
        if (response.data !== undefined) {
          setData(response.data);
          setNoData(true);
        } else {
          setData([]);
        }
      });
    }
    setLoading(false);
  }, [Gfunc]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const handleOnEdit = useCallback(
    async (e, row, att) => {
      const edt1 = e.target.innerHTML;
      const edt = Gfunc.DeleteSpace(edt1);
      if (row[att] !== edt && !(!edt && !row[att])) {
        const attrib = row[att];
        const response = await Gfunc.axiosPostAction(service, "upd", {
          id: row.id || row.service,
          att: att,
          val: edt,
        });
        if (response.data.err && response.data.err !== "-1") {
          handleAlert(true, "error", response.data.err);
          e.target.innerHTML = attrib;
        } else {
          Gfunc.axiosResponse(response, () => {
            const agent = localStorage.getItem("name");
            const date_upd = Gfunc.getDate();
            row[att] = edt;
            row.agent = agent;
            row.date_upd = date_upd;
            e.target.innerHTML = edt;
            setData((prevData) =>
              prevData.map((item) =>
                item.service === row.service
                  ? { ...item, [att]: edt, agent, date_upd }
                  : item,
              ),
            );
          });
        }
      } else {
        e.target.innerHTML = edt;
      }
    },
    [Gfunc],
  );

  const handleStatusChange = useCallback(
    async (row, newStatus) => {
      const response = await Gfunc.axiosPostAction(service, "upd", {
        id: row.id || row.service,
        att: "statu",
        val: newStatus,
      });
      if (response.data.err && response.data.err !== "-1") {
        handleAlert(true, "error", response.data.err);
      } else {
        Gfunc.axiosResponse(response, () => {
          // Update local data without refreshing
          setData((prevData) =>
            prevData.map((item) =>
              item.service === row.service
                ? { ...item, statu: newStatus }
                : item,
            ),
          );
        });
      }
    },
    [Gfunc],
  );

  const handleSelect = (row, name) => {
    const rowId = row.service;
    setItem(rowId);
    setSelecto(row[name]);
    setItemN(name);
  };

  // ============ STATUS BADGE - COMPACT ============
  const StatusBadge = ({ status, expireDate }) => {
    const isStopped = isServiceStopped(status, expireDate);
    let icon, color, label, bgColor;

    if (isStopped) {
      icon = mdiAlertCircleOutline;
      color = "#c62828";
      bgColor = "#ffebee";
      label = "Innactif";
    } else {
      icon = mdiCheckCircleOutline;
      color = "#2e7d32";
      bgColor = "#e8f5e9";
      label = "Actif";
    }

    return (
      <Chip
        icon={<Icon path={icon} size={0.6} color={color} />}
        label={label}
        style={{
          backgroundColor: bgColor,
          color: color,
          fontWeight: 600,
          fontSize: 11,
          height: 16,
          borderRadius: 4,
        }}
      />
    );
  };

  // ============ STATUS SWITCH - Follows ONLY statu ============
  const StatusSwitch = ({ row, display = true }) => {
    // checked = true means service is disabled (statu === 0)
    // checked = false means service is active (statu === 1)
    const [checked, setChecked] = useState(row.statu === 0);
    const handleChange = async (event) => {
      const isChecked = event.target.checked;
      setChecked(isChecked);
      const newStatus = isChecked ? 0 : 1; // 0 = disabled, 1 = active
      await handleStatusChange(row, newStatus);
    };

    // Update checked state when row.statu changes from parent
    useEffect(() => {
      setChecked(row.statu === 0);
    }, [row.statu]);

    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent={"center"}
        style={{ gap: 6 }}
      >
        {display && (
          <Typography
            style={{
              fontSize: 9,
              fontWeight: 600,
              color: checked ? "#c62828" : "#000",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            {"Désactivé"}
          </Typography>
        )}
        <RedSwitch size="small" checked={checked} onChange={handleChange} />
      </Box>
    );
  };

  // ============ EDITABLE FIELD - COMPACT ============
  const EditableField = ({
    value,
    row,
    attribute,
    placeholder = "",
    language = "",
  }) => {
    return (
      <ContentEditable
        html={value || ""}
        className="content-editable"
        disabled={false}
        onBlur={(e) => handleOnEdit(e, row, attribute)}
        placeholder={placeholder}
        style={{
          wordWrap: "break-word",
          whiteSpace: "pre-wrap",
          overflowWrap: "break-word",
          maxWidth: "100%",
          maxHeight: focused ? "none" : "78px", // 👈 clamp only when not editing
          display: "-webkit-box", // 👈 needed for line-clamp
          WebkitLineClamp: focused ? "unset" : 2, //
          padding: "2px 0",
          minHeight: "42px",
          width: "100%",
          height: "fit-content",
          fontSize: 12,
          lineHeight: "15px",
          // direction: language === "ar" ? "rtl" : "ltr",
          // textAlign: language === "ar" ? "right" : "left",
          textAlign: "left",
        }}
      />
    );
  };

  // ============ CARD VIEW - COMPACT ============
  const ServiceCard = ({ service: item }) => {
    const isStopped = isServiceStopped(item.statu, item.expire_date);
    const isActive = item.statu === 1;

    let cardStyles = {
      backgroundColor: "white",
      border: "1px solid #e2e8f0",
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    };

    if (isStopped) {
      cardStyles = {
        backgroundColor: "#fffafa",
        border: "2px solid #fc8181",
        boxShadow: "0 2px 8px rgba(198, 40, 40, 0.08)",
      };
    } else {
      cardStyles = {
        backgroundColor: "white",
        border: "2px solid #68d391",
        boxShadow: "0 2px 8px rgba(46, 125, 50, 0.06)",
      };
    }

    const cardKey = item.service;

    return (
      <Card
        key={cardKey}
        elevation={0}
        style={{
          borderRadius: 8,
          height: "100%",
          transition: "all 0.2s ease",
          ...cardStyles,
        }}
      >
        <CardContent style={{ padding: "12px 16px" }}>
          {/* Header with Service Name and Status */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={1}
          >
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <Typography
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: isStopped ? "#c62828" : "#1a202c",
                  letterSpacing: "-0.01em",
                }}
              >
                {item.service}
              </Typography>
              {item.dateupd && (
                <Tooltip
                  title={`Dernière modification: ${item.dateupd} par ${item.agentupd || "—"}`}
                  arrow
                >
                  <Icon path={mdiPencilOutline} size={0.4} color="#a0aec0" />
                </Tooltip>
              )}
            </Box>
            <StatusBadge status={item.statu} expireDate={item.expire_date} />
          </Box>

          <Divider style={{ marginBottom: 8, backgroundColor: "#edf2f7" }} />

          {/* Messages Section - Compact */}
          <Box mb={1.5}>
            <Box display="flex" alignItems="flex-start" mb={0.5}>
              <Box minWidth={24}>
                <Typography
                  style={{
                    fontSize: 8,
                    fontWeight: 700,
                    color: "#a0aec0",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    paddingTop: 2,
                  }}
                >
                  FR
                </Typography>
              </Box>
              <Box flex={1} style={{ marginRight: 4 }}>
                <EditableField
                  value={item.message_fr}
                  row={item}
                  attribute="message_fr"
                  placeholder="Cliquez pour modifier..."
                />
              </Box>
            </Box>

            <Box display="flex" alignItems="flex-start" mb={0.5}>
              <Box minWidth={24}>
                <Typography
                  style={{
                    fontSize: 8,
                    fontWeight: 700,
                    color: "#a0aec0",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    paddingTop: 2,
                  }}
                >
                  AR
                </Typography>
              </Box>
              <Box flex={1}>
                <EditableField
                  value={item.message_ar}
                  row={item}
                  attribute="message_ar"
                  placeholder="Cliquez pour modifier..."
                  language="ar"
                />
              </Box>
            </Box>

            <Box display="flex" alignItems="flex-start">
              <Box minWidth={24}>
                <Typography
                  style={{
                    fontSize: 8,
                    fontWeight: 700,
                    color: "#a0aec0",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    paddingTop: 2,
                  }}
                >
                  EN
                </Typography>
              </Box>
              <Box flex={1}>
                <EditableField
                  value={item.message_en}
                  row={item}
                  attribute="message_en"
                  placeholder="Cliquez pour modifier..."
                />
              </Box>
            </Box>
          </Box>

          <Divider style={{ marginBottom: 8, backgroundColor: "#edf2f7" }} />

          {/* Footer with Status Control, Date, and Agent - Compact */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            style={{ gap: 8, fontSize: 12 }}
          >
            <Box display="flex" alignItems="center" style={{ gap: 10 }}>
              <StatusSwitch row={item} />
              {"jusqu'à "}
              <Box display="flex" alignItems="center" style={{ gap: 2 }}>
                <DateTimePickerUpd
                  key={`datepicker-${item.service}`}
                  service={service}
                  action="upd"
                  name="expire_date"
                  row={item}
                  selecto={selecto}
                  ItemN={ItemN}
                  Item={Item}
                  setItem={setItem}
                  setSelecto={setSelecto}
                  handleSelect={handleSelect}
                  handleAlert={handleAlert}
                  rowId="service"
                  dateupd={item.dateupd}
                  agentupd={item.agentupd}
                  datein={item.datein}
                  agentin={item.agentin}
                  extra=""
                  minDate={new Date(2020, 0, 1)}
                  maxDate={null}
                />
              </Box>
            </Box>
            <Tooltip
              title={`Dernière modification: ${item.date_upd || "—"} par ${item.agent || "—"}`}
              arrow
            >
              <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                {/* {"executé par "} */}
                <Icon path={mdiAccount} size={0.6} color="#a0aec0" />
                <Typography
                  style={{ fontSize: 10, color: "#71e96", fontWeight: 500 }}
                >
                  {item.agent || "—"}
                </Typography>
                <Typography
                  style={{ fontSize: 10, color: "#71e96", fontWeight: 500 }}
                >
                  {item.date_upd && <strong>—</strong>}
                  {item.date_upd && ` ${item.date_upd} `}
                </Typography>
              </Box>
            </Tooltip>
          </Box>

          {/* Display agentupd and dateupd info - Compact */}
          {/* <Box mt={1} pt={0.5} borderTop="1px solid #edf2f7">
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography style={{ fontSize: 8, color: "#a0aec0" }}>
                <strong>Créé:</strong> {item.agentin || "—"}
                {item.datein && ` (${item.datein})`}
              </Typography>
              <Typography style={{ fontSize: 8, color: "#a0aec0" }}>
                <strong>Modifié:</strong> {item.user_upd || "—"}
                {item.date_upd && ` (${item.date_upd})`}
              </Typography>
            </Box>
          </Box> */}
        </CardContent>
      </Card>
    );
  };

  // ============ TABLE COLUMNS WITH RED BACKGROUND FOR INACTIVE ============
  const columns = [
    {
      key: "service",
      header: "Service",
      width: "10%",
      render: (value, row) => {
        const isStopped = isServiceStopped(row.statu, row.expire_date);
        return (
          <Typography
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: isStopped ? "#c62828" : "#1a202c",
            }}
          >
            {value}
          </Typography>
        );
      },
    },
    {
      key: "status",
      header: "État",
      width: "10%",
      render: (_, row) => {
        return <StatusBadge status={row.statu} expireDate={row.expire_date} />;
      },
    },
    {
      key: "message_fr",
      header: "Français",
      width: "14%",
      render: (value, row) => (
        <EditableField
          value={value}
          row={row}
          attribute="message_fr"
          placeholder="—"
        />
      ),
    },
    {
      key: "message_ar",
      header: "Arabe",
      width: "14%",
      render: (value, row) => (
        <EditableField
          value={value}
          row={row}
          attribute="message_ar"
          placeholder="—"
          language="ar"
        />
      ),
    },
    {
      key: "message_en",
      header: "Anglais",
      width: "14%",
      render: (value, row) => (
        <EditableField
          value={value}
          row={row}
          attribute="message_en"
          placeholder="—"
        />
      ),
    },
    {
      key: "expire_date",
      header: "Expiration",
      width: "12%",
      render: (value, row) => (
        <DateTimePickerUpd
          key={`table-datepicker-${row.service}`}
          service={service}
          action="upd"
          name="expire_date"
          row={row}
          selecto={selecto}
          ItemN={ItemN}
          Item={Item}
          setItem={setItem}
          setSelecto={setSelecto}
          handleSelect={handleSelect}
          handleAlert={handleAlert}
          rowId="service"
          dateupd={row.dateupd}
          agentupd={row.agentupd}
          datein={row.datein}
          agentin={row.agentin}
          extra=""
          minDate={new Date(2020, 0, 1)}
          maxDate={null}
        />
      ),
    },
    {
      key: "agent",
      header: "Agent",
      width: "8%",
      render: (value, row) => (
        <Box display="flex" alignItems="center" style={{ gap: 4 }}>
          <Typography style={{ fontSize: 12, color: "#4a5568" }}>
            {value || "—"}
          </Typography>
        </Box>
      ),
    },
    {
      key: "status_switch",
      header: "Désactivé",
      width: "10%",
      align: "center",
      render: (_, row) => <StatusSwitch row={row} display={false} />,
    },
  ];

  // ============ RENDER ============
  return (
    <div className="content">
      <AlertCompo
        severity={severity}
        open={open}
        setOpen={setOpen}
        message={messageA}
      />

      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={2}
        py={1.5}
      >
        <Typography
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: "#1a202c",
          }}
        >
          État des services
        </Typography>

        <Box
          display="flex"
          alignItems="center"
          style={{
            gap: 2,
            padding: 3,
            border: "1px solid #ddd",
            borderRadius: 8,
            backgroundColor: "#f7f7f7",
          }}
        >
          <Button
            onClick={() => setViewMode("cards")}
            variant="contained"
            disableElevation
            style={{
              minWidth: 36,
              width: 36,
              height: 32,
              padding: 0,
              borderRadius: 6,
              backgroundColor: viewMode === "cards" ? "#fff" : "transparent",
              color: viewMode === "cards" ? "#333" : "#888",
              boxShadow:
                viewMode === "cards" ? "0 1px 3px rgba(0,0,0,0.15)" : "none",
            }}
          >
            <ViewModule fontSize="small" />
          </Button>

          <Button
            onClick={() => setViewMode("table")}
            variant="contained"
            disableElevation
            style={{
              minWidth: 36,
              width: 36,
              height: 32,
              padding: 0,
              borderRadius: 6,
              backgroundColor: viewMode === "table" ? "#fff" : "transparent",
              color: viewMode === "table" ? "#333" : "#888",
              boxShadow:
                viewMode === "table" ? "0 1px 3px rgba(0,0,0,0.15)" : "none",
            }}
          >
            <ViewList fontSize="small" />
          </Button>
        </Box>
      </Box>

      <Divider style={{ backgroundColor: "#edf2f7" }} />

      {/* Content */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={6}>
          <CircularProgress size={35} />
        </Box>
      ) : notAllowed ? (
        <NotAllowedComponent message={errMessage} />
      ) : !data.length ? (
        <NoDataComponent message="Aucun service disponible" />
      ) : viewMode === "cards" ? (
        <Grid container spacing={2} style={{ padding: 16 }}>
          {data.map((item) => (
            <Grid item xs={12} md={6} key={item.service}>
              <ServiceCard service={item} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box style={{ padding: 16, overflow: "auto" }}>
          <DynamicTable
            data={data.map((row) => {
              const isInactive = row.statu !== 0;
              return {
                ...row,
                _rowStyle: isInactive
                  ? { backgroundColor: "#ffebee !important" }
                  : {},
              };
            })}
            columns={columns}
            rowKey="service"
            loading={false}
            emptyMessage="Aucun service disponible"
          />
        </Box>
      )}
    </div>
  );
}
